import React from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import {
  FolderOpen,
  AccountBalance,
  AttachMoney,
  CheckCircle,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { brand, status, text, border } from '../../theme/colors';

/**
 * Dashboard Stats Cards
 * Displays key metrics with clickable cards for filtering
 * Modern design with color coding and trend indicators
 */
const DashboardStatsCards = ({ stats: dashboardStats, projects, onFilterChange }) => {
  // Use provided stats or calculate from projects as fallback
  const totalProjects = dashboardStats?.totalProjects || projects.length;
  const activeProjects = dashboardStats?.activeProjects || projects.filter(p => 
    p.status === 'in_progress' || p.status === 'planning' || p.status === 'open_for_bids'
  ).length;
  const completedProjects = dashboardStats?.completedProjects || projects.filter(p => p.status === 'completed').length;
  
  const totalBudget = dashboardStats?.totalInvested || projects.reduce((sum, p) => sum + parseFloat(p.budget || 0), 0);
  
  // Calculate wallet balance (use provided stats or calculate)
  const escrowAmount = dashboardStats?.fundsInEscrow || projects
    .filter(p => p.status === 'in_progress')
    .reduce((sum, p) => sum + parseFloat(p.budget || 0), 0);
  const escrowProjects = dashboardStats?.activeEscrows || projects.filter(p => p.status === 'in_progress').length;

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === 0) return '$0';
    if (amount < 1000) return `$${amount.toFixed(0)}`;
    if (amount < 1000000) return `$${(amount / 1000).toFixed(1)}K`;
    return `$${(amount / 1000000).toFixed(1)}M`;
  };

  const stats = [
    {
      title: 'Total Projects',
      value: totalProjects,
      icon: <FolderOpen sx={{ fontSize: 32, color: brand.primary }} />,
      iconBg: `${brand.primary}15`,
      subtitle: `${activeProjects} active`,
      trend: activeProjects > 0 ? 'up' : null,
      filterAction: () => onFilterChange && onFilterChange({ status: 'all', sortBy: 'recent' })
    },
    {
      title: 'In Wallet',
      value: formatCurrency(escrowAmount),
      icon: <AccountBalance sx={{ fontSize: 32, color: '#16a085' }} />,
      iconBg: '#16a08515',
      subtitle: `${escrowProjects} projects`,
      trend: escrowProjects > 0 ? 'up' : 'down',
      filterAction: () => onFilterChange && onFilterChange({ status: 'in_progress', sortBy: 'recent' })
    },
    {
      title: 'Total Invested',
      value: formatCurrency(totalBudget),
      icon: <AttachMoney sx={{ fontSize: 32, color: brand.secondary }} />,
      iconBg: 'rgba(194,65,12,0.08)',
      subtitle: `${projects.length} projects`,
      trend: totalBudget > 0 ? 'up' : null,
      filterAction: () => onFilterChange && onFilterChange({ status: 'all', sortBy: 'budget_high' })
    },
    {
      title: 'Completed',
      value: completedProjects,
      icon: <CheckCircle sx={{ fontSize: 32, color: completedProjects > 0 ? '#43B581' : '#999' }} />,
      iconBg: completedProjects > 0 ? `${status.success}15` : '#99999915',
      subtitle: completedProjects > 0 ? `${((completedProjects / totalProjects) * 100).toFixed(0)}% complete` : 'Building portfolio',
      trend: completedProjects > 0 ? 'up' : null,
      filterAction: () => onFilterChange && onFilterChange({ status: 'completed', sortBy: 'recent' })
    }
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={2}>
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={6} md={3} key={index}>
            <Card
              onClick={stat.filterAction}
              sx={{
                height: '100%',
                bgcolor: 'white',
                border: `2px solid ${border.light}`,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: brand.primary,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: stat.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: text.secondary,
                        fontWeight: 500,
                        fontSize: '12px',
                        mb: 0.5
                      }}
                    >
                      {stat.title}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        fontSize: '24px',
                        lineHeight: 1,
                        color: text.primary
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: text.secondary,
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      {stat.trend === 'up' && <TrendingUp sx={{ fontSize: 12, color: status.success }} />}
                      {stat.trend === 'down' && <TrendingDown sx={{ fontSize: 12, color: status.errorDark }} />}
                      {stat.subtitle}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardStatsCards;
